import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { buyerOrdersService } from '../../../services/buyerService';

const ROIInsights = ({ language, startDate, endDate }) => {
  const [insights, setInsights] = useState({
    costSavings: 0,
    qualityScore: 0,
    supplierPerformance: 0,
    totalOrders: 0
  });
  const [loading, setLoading] = useState(true);

  const content = {
    en: {
      title: 'ROI Insights',
      costSavings: 'Cost Savings',
      qualityImprovement: 'Quality Score',
      supplierPerformance: 'Supplier Performance',
      totalOrders: 'Total Orders',
      vsLastPeriod: 'vs last period',
      noData: 'No ROI data available'
    },
    zh: {
      title: 'ROI洞察',
      costSavings: '成本节约',
      qualityImprovement: '质量评分',
      supplierPerformance: '供应商表现',
      totalOrders: '总订单',
      vsLastPeriod: '与上期相比',
      noData: '没有ROI数据'
    }
  };

  const t = content?.[language];

  useEffect(() => {
    loadROIData();
  }, [startDate, endDate]);

  const loadROIData = async () => {
    try {
      setLoading(true);
      const orders = await buyerOrdersService?.getAll();

      // Filter by date range
      const filtered = orders?.filter(order => {
        const orderDate = new Date(order?.createdAt);
        return orderDate >= startDate && orderDate <= endDate;
      });

      // Calculate insights (mock calculations for demonstration)
      const totalOrders = filtered?.length;
      const costSavings = totalOrders * 150; // Mock: $150 savings per order
      const qualityScore = 87; // Mock quality score
      const supplierPerformance = 92; // Mock supplier performance

      setInsights({
        costSavings,
        qualityScore,
        supplierPerformance,
        totalOrders
      });
    } catch (error) {
      console.error('Failed to load ROI data:', error);
    } finally {
      setLoading(false);
    }
  };

  const insightsData = [
    {
      id: 1,
      label: t?.costSavings,
      value: `$${insights?.costSavings?.toLocaleString()}`,
      trend: '+12%',
      trendUp: true,
      icon: 'DollarSign',
      color: 'success'
    },
    {
      id: 2,
      label: t?.qualityImprovement,
      value: `${insights?.qualityScore}/100`,
      trend: '+5%',
      trendUp: true,
      icon: 'Award',
      color: 'primary'
    },
    {
      id: 3,
      label: t?.supplierPerformance,
      value: `${insights?.supplierPerformance}%`,
      trend: '+8%',
      trendUp: true,
      icon: 'TrendingUp',
      color: 'secondary'
    },
    {
      id: 4,
      label: t?.totalOrders,
      value: insights?.totalOrders,
      trend: '+15%',
      trendUp: true,
      icon: 'ShoppingBag',
      color: 'accent'
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      success: 'bg-success/10 text-success',
      primary: 'bg-primary/10 text-primary',
      secondary: 'bg-secondary/10 text-secondary',
      accent: 'bg-accent/10 text-accent-foreground'
    };
    return colors?.[color] || colors?.primary;
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-center h-64">
          <Icon name="Loader2" size={32} className="animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center gap-2 mb-6">
        <Icon name="TrendingUp" size={20} className="text-primary" />
        <h3 className="text-lg font-heading font-semibold text-foreground">{t?.title}</h3>
      </div>
      <div className="space-y-4">
        {insightsData?.map((insight) => (
          <div key={insight?.id} className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${getColorClasses(insight?.color)}`}>
                <Icon name={insight?.icon} size={20} />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{insight?.label}</p>
                <p className="text-xl font-bold text-foreground">{insight?.value}</p>
              </div>
            </div>
            <div className={`flex items-center gap-1 text-sm font-medium ${
              insight?.trendUp ? 'text-success' : 'text-destructive'
            }`}>
              <Icon name={insight?.trendUp ? 'ArrowUp' : 'ArrowDown'} size={16} />
              <span>{insight?.trend}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ROIInsights;